import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { TimelinePlaybackEngine, type EngineSources } from "./playback-engine"
import { readPlayerAudioScrubPref } from "./player-prefs"
import type { TimelineDocument } from "./types"

export type TimelinePlaybackControls = {
  play: (rate?: number) => void
  pause: () => void
  toggle: () => void
  seek: (ms: number) => void
  /** Перетаскивание плейхеда: сик коалесится до одного за кадр анимации. */
  scrubTo: (ms: number) => void
  /** Конец перетаскивания: финальный точный сик. */
  endScrub: () => void
  /** Идёт ли сейчас перетаскивание плейхеда. */
  isScrubbing: () => boolean
  setLoop: (loop: boolean) => void
  setMasterVolume: (volume: number) => void
  setMasterMuted: (muted: boolean) => void
  /** Слышимый скраб (гранулы Web Audio при перетаскивании плейхеда). */
  setAudioScrub: (enabled: boolean) => void
  /** iPhone: нативный системный фулскрин активного видеоэлемента. false — недоступен. */
  enterNativeVideoFullscreen: () => boolean
  getRate: () => number
  isPlaying: () => boolean
  currentMs: () => number
  /** Цель захвата кадра под плейхедом (версия + source-мс). null — gap, скрытая дорожка или аниматик. */
  captureTarget: () => { versionId: number | null; sourceMs: number } | null
  /** Снимок активного video под плейхедом (PNG→Image). */
  captureCurrentFrame: () => Promise<HTMLImageElement>
}

/**
 * Позиция плейхеда как внешний store: обновляется каждый rAF-тик, поэтому
 * не хранится в React-state — иначе весь редактор ререндерился бы 60 раз/сек.
 * Потребители (таймкод, плейхед) подписываются и пишут в DOM императивно.
 */
export type PlaybackTimeStore = {
  get: () => number
  subscribe: (listener: () => void) => () => void
}

/**
 * React-обёртка над TimelinePlaybackEngine: создаёт движок при монтировании
 * вьюпорта, прокидывает документ/источники и отдаёт state + управление.
 */
export function useTimelinePlayback(params: {
  doc: TimelineDocument
  sources: EngineSources
  onVersionDuration?: (versionId: number, durationMs: number) => void
  onVersionMediaSize?: (versionId: number, size: { width: number; height: number }) => void
  /** Конец воспроизведения вперёд без цикла. */
  onEnded?: () => void
  onAutoplayBlocked?: () => void
}) {
  const { doc, sources, onVersionDuration, onVersionMediaSize, onEnded, onAutoplayBlocked } = params
  const engineRef = useRef<TimelinePlaybackEngine | null>(null)
  const [viewportEl, setViewportEl] = useState<HTMLDivElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [buffering, setBuffering] = useState(false)

  const timeRef = useRef(0)
  const timeListenersRef = useRef(new Set<() => void>())
  const timeStore = useMemo<PlaybackTimeStore>(() => {
    const listeners = timeListenersRef.current
    return {
      get: () => timeRef.current,
      subscribe: (listener) => {
        listeners.add(listener)
        return () => {
          listeners.delete(listener)
        }
      },
    }
  }, [])

  const onVersionDurationRef = useRef(onVersionDuration)
  onVersionDurationRef.current = onVersionDuration
  const onVersionMediaSizeRef = useRef(onVersionMediaSize)
  onVersionMediaSizeRef.current = onVersionMediaSize
  const onEndedRef = useRef(onEnded)
  onEndedRef.current = onEnded
  const onAutoplayBlockedRef = useRef(onAutoplayBlocked)
  onAutoplayBlockedRef.current = onAutoplayBlocked
  // setLoop/volume/muted могут вызваться до появления viewport → engine ещё null.
  // Кладём желаемые значения в ref и применяем при создании движка. audioScrub —
  // сразу из префа: дефолт `true` включал бы скраб на iOS, если движок вдруг
  // создастся раньше синхронизирующего эффекта потребителя.
  const pendingSettingsRef = useRef({ loop: false, volume: 1, muted: false, audioScrub: readPlayerAudioScrubPref() })

  const viewportRef = useCallback((el: HTMLDivElement | null) => {
    setViewportEl(el)
  }, [])

  useEffect(() => {
    if (!viewportEl) return
    const engine = new TimelinePlaybackEngine(viewportEl, {
      onTime: (ms) => {
        timeRef.current = ms
        for (const listener of timeListenersRef.current) listener()
      },
      onPlayingChange: (p) => setPlaying(p),
      onBuffering: (b) => setBuffering(b),
      onVersionDuration: (versionId, durationMs) => onVersionDurationRef.current?.(versionId, durationMs),
      onVersionMediaSize: (versionId, size) => onVersionMediaSizeRef.current?.(versionId, size),
      onEnded: () => onEndedRef.current?.(),
      // Fallback «глушим и играем» только у плеера, который синхронизирует кнопку звука.
      onAutoplayBlocked: onAutoplayBlockedRef.current ? () => onAutoplayBlockedRef.current?.() : undefined,
    })
    const pending = pendingSettingsRef.current
    engine.setLoop(pending.loop)
    engine.setMasterVolume(pending.volume)
    engine.setMasterMuted(pending.muted)
    engine.setAudioScrub(pending.audioScrub)
    engineRef.current = engine
    return () => {
      engine.dispose()
      engineRef.current = null
    }
  }, [viewportEl])

  useEffect(() => {
    engineRef.current?.setTimeline(doc, sources)
  }, [doc, sources, viewportEl])

  const controls = useMemo<TimelinePlaybackControls>(
    () => ({
      play: (rate) => engineRef.current?.play(rate),
      pause: () => engineRef.current?.pause(),
      toggle: () => engineRef.current?.toggle(),
      seek: (ms) => engineRef.current?.seek(ms),
      scrubTo: (ms) => engineRef.current?.scrubTo(ms),
      endScrub: () => engineRef.current?.endScrub(),
      isScrubbing: () => engineRef.current?.isScrubbing() ?? false,
      setLoop: (loop) => {
        pendingSettingsRef.current.loop = loop
        engineRef.current?.setLoop(loop)
      },
      setMasterVolume: (volume) => {
        pendingSettingsRef.current.volume = volume
        engineRef.current?.setMasterVolume(volume)
      },
      setMasterMuted: (muted) => {
        pendingSettingsRef.current.muted = muted
        engineRef.current?.setMasterMuted(muted)
      },
      setAudioScrub: (enabled) => {
        pendingSettingsRef.current.audioScrub = enabled
        engineRef.current?.setAudioScrub(enabled)
      },
      enterNativeVideoFullscreen: () => engineRef.current?.enterNativeVideoFullscreen() ?? false,
      getRate: () => engineRef.current?.getRate() ?? 1,
      isPlaying: () => engineRef.current?.isPlaying() ?? false,
      currentMs: () => engineRef.current?.currentMs() ?? 0,
      captureTarget: () => engineRef.current?.captureTarget() ?? null,
      captureCurrentFrame: () => {
        const engine = engineRef.current
        if (!engine) return Promise.reject(new Error("Video frame unavailable"))
        return engine.captureCurrentFrame()
      },
    }),
    [],
  )

  return { viewportRef, controls, timeStore, playing, buffering }
}
